import React from 'react';
import { Link } from 'react-router-dom';
import type { RecoverPasswordProps } from './RecoverPassword.types';
import { useRecoverPassword } from './RecoverPassword.hook';
import * as styles from './RecoverPassword.styles';
import { getLangTitle } from './RecoverPassword.utils';

export const RecoverPassword: React.FC<RecoverPasswordProps> = () => {
  const {
    t,
    i18n,
    token,
    tokenStatus,
    validatingToken,
    password,
    setPassword,
    passwordConfirmation,
    setPasswordConfirmation,
    message,
    errorMessage,
    loading,
    handleSubmit,
    changeLanguage,
  } = useRecoverPassword();

  return (
    <div className={styles.container}>
      {/* Background glow effects */}
      <div className={styles.bgGlowPrimary}></div>
      <div className={styles.bgGlowAccent}></div>

      {/* Main glass card container */}
      <div className={styles.glassCard}>
        
        {/* Language switches & Header */}
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            <img 
              src="/logo.svg" 
              alt="WealthIQ Icon" 
              className={styles.logoImg} 
            />
            <span className={styles.titleText}>
              Wealth<span className={styles.titleHighlight}>
                IQ
                <span className={styles.titleBadge}>
                  Pro
                </span>
              </span>
            </span>
          </div>

          {/* Translation Controls */}
          <div className={styles.langControlsContainer}>
            {(['en', 'hi', 'te', 'pa'] as const).map((lang) => {
              const isActive = (i18n.language || 'en').startsWith(lang);
              return (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={styles.getLangBtnClass(isActive)}
                  title={getLangTitle(lang)}
                >
                  {lang}
                </button>
              );
            })}
          </div>
        </div>

        {validatingToken ? (
          <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="validating-token-loader">
            <i className="fa-solid fa-circle-notch animate-spin text-3xl text-emerald-400 mb-3"></i>
            <p className="text-gray-300 text-sm">{t('checkingToken', 'Validating reset link...')}</p>
          </div>
        ) : tokenStatus === 'ALREADY_RESET' ? (
          <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="already-reset-card">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mb-4">
              <i className="fa-solid fa-circle-check text-2xl text-emerald-400"></i>
            </div>
            <h2 className="text-xl font-bold text-white mb-2" data-testid="already-reset-title">
              {t('passwordAlreadyReset', 'password is already reset')}
            </h2>
            <p className="text-gray-300 text-sm mb-6">
              {t('passwordAlreadyResetSub', 'Your password has already been reset using this link.')}
            </p>
            <Link
              to="/login"
              className={styles.submitButton}
              style={{ textDecoration: 'none', display: 'inline-flex', justifyContent: 'center' }}
              data-testid="back-to-login-btn"
            >
              <i className="fa-solid fa-arrow-left"></i>
              <span>{t('backToLoginText', 'back to login')}</span>
            </Link>
          </div>
        ) : tokenStatus === 'EXPIRED' ? (
          <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="expired-link-card">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mb-4">
              <i className="fa-solid fa-clock-rotate-left text-2xl text-amber-400"></i>
            </div>
            <h2 className="text-xl font-bold text-white mb-2" data-testid="expired-link-title">
              {t('linkNoLongerValid', 'This link is no longer valid')}
            </h2>
            <p className="text-gray-300 text-sm mb-6">
              {t('linkNoLongerValidSub', 'This reset link has expired after 30 minutes. Please request a new link.')}
            </p>
            <Link
              to="/login"
              className={styles.submitButton}
              style={{ textDecoration: 'none', display: 'inline-flex', justifyContent: 'center' }}
              data-testid="back-to-login-btn"
            >
              <i className="fa-solid fa-arrow-left"></i>
              <span>{t('backToLoginText', 'back to login')}</span>
            </Link>
          </div>
        ) : (
          <>
            {/* Heading */}
            <div className={styles.headingContainer}>
              <h2 className={styles.headingTitle}>
                {t('setNewPassword', 'Set New Password')}
              </h2>
              <p className={styles.headingSubtitle}>
                {t('setNewPasswordSubtitle', 'Enter your new password below.')}
              </p>
            </div>

            {/* Status Messages */}
            {errorMessage && (
              <div className={styles.errorContainer}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                <span>{errorMessage}</span>
              </div>
            )}

            {message && (
              <div className={styles.successContainer}>
                <i className="fa-solid fa-circle-check"></i>
                <span>{message}</span>
              </div>
            )}

            {!token || tokenStatus === 'INVALID' ? (
              <div className={styles.missingTokenContainer}>
                {t('missingToken', 'Invalid password reset link. Please request a new one.')}
              </div>
            ) : (
              /* Recover Password Form */
              <form onSubmit={handleSubmit} className={styles.formContainer}>
                <div className={styles.inputGroup}>
                  <label htmlFor="recover-password" className={styles.label}>
                    {t('newPassword', 'New Password')}
                  </label>
                  <div className={styles.inputWrapper}>
                    <i className={`${styles.iconClass} fa-lock`}></i>
                    <input
                      id="recover-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={styles.inputField}
                      required
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="recover-password-confirm" className={styles.label}>
                    {t('confirmPassword', 'Confirm New Password')}
                  </label>
                  <div className={styles.inputWrapper}>
                    <i className={`${styles.iconClass} fa-lock`}></i>
                    <input
                      id="recover-password-confirm"
                      type="password"
                      placeholder="••••••••"
                      value={passwordConfirmation}
                      onChange={(e) => setPasswordConfirmation(e.target.value)}
                      className={styles.inputField}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !!message}
                  className={styles.submitButton}
                >
                  {loading ? (
                    <>
                      <i className="fa-solid fa-circle-notch animate-spin"></i>
                      <span>{t('savingPassword', 'Saving...')}</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i>
                      <span>{t('savePassword', 'Save Password')}</span>
                    </>
                  )}
                </button>
              </form>
            )}

            <div className={styles.backLinkContainer}>
              <Link to="/login" className={styles.backLinkClass}>
                {t('backToLoginText', 'back to login')}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecoverPassword;
